import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Spinner,
  Badge,
  Breadcrumb,
} from 'react-bootstrap'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import usePermissionEvents, {
  PermissionEventFilters,
} from 'hooks/use-permission-events'
import { DEFAULT_PERMISSION_EVENTS_PAGE_SIZE } from 'constants/filters'
import usePermissionEventsSummary from 'hooks/use-permission-events-summary'
import useUserDetail from 'hooks/use-user-detail'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import PermissionEventsTable from 'components/permission-events-table'
import PermissionEventsFilters from 'components/permission-events-filters'
import IndividualBehavioralProfileModal from 'components/individual-behavioral-profile-modal'
import useIndividualBehavioralProfile from 'hooks/use-individual-behavioral-profile'
import { RootState } from 'index'

const UserPermissionEventsPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>()
  const history = useHistory()
  const currentUser = useSelector((state: RootState) => state.user.currentUser)

  const [filters, setFilters] = useState<PermissionEventFilters>({
    limit: DEFAULT_PERMISSION_EVENTS_PAGE_SIZE,
  })
  const [allEvents, setAllEvents] = useState<any[]>([])
  const [currentCursor, setCurrentCursor] = useState<string | undefined>()

  // Individual Behavioral Profile Modal state
  const [showIndividualModal, setShowIndividualModal] = useState(false)
  const [selectedSystemUser, setSelectedSystemUser] = useState<string>('')
  const [selectedRecipient, setSelectedRecipient] = useState<string>('')

  // Get user details to extract phone number
  const {
    data: userDetail,
    isLoading: isUserLoading,
    error: userError,
  } = useUserDetail(uuid)

  // Get Apple ID details to extract phone number (like in UserDetailPage)
  const { data: appleId } = useGetAppleIdByUserId(userDetail?.user?.id || '')

  // Extract phone number from user detail (matching UserDetailPage structure)
  const phoneNumber =
    appleId?.phone_number || userDetail?.user?.worknumber?.number

  // Permission events query
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = usePermissionEvents(
    phoneNumber || '',
    { ...filters, cursor: currentCursor },
    !!phoneNumber
  )

  // Summary for last 7 days
  const last7Days = new Date()
  last7Days.setDate(last7Days.getDate() - 7)
  const today = new Date()

  const { data: summaryData, isLoading: isSummaryLoading } =
    usePermissionEventsSummary(
      phoneNumber || '',
      last7Days.toISOString().split('T')[0],
      today.toISOString().split('T')[0],
      undefined,
      !!phoneNumber
    )

  // Individual behavioral profile hook
  const {
    data: individualProfileData,
    isLoading: isIndividualProfileLoading,
    error: individualProfileError,
  } = useIndividualBehavioralProfile(
    selectedSystemUser,
    selectedRecipient,
    showIndividualModal && !!selectedSystemUser && !!selectedRecipient
  )

  // Handle filter changes
  const handleFilterChange = (newFilters: PermissionEventFilters) => {
    setFilters(newFilters)
    setAllEvents([]) // Reset events when filters change
    setCurrentCursor(undefined) // Reset cursor
  }

  // Handle load more
  const handleLoadMore = () => {
    if (eventsData?.pagination.nextCursor) {
      setCurrentCursor(eventsData.pagination.nextCursor)
    }
  }

  // Handle permission event click
  const handleEventClick = (systemUser: string, recipient: string) => {
    setSelectedSystemUser(systemUser)
    setSelectedRecipient(recipient)
    setShowIndividualModal(true)
  }

  // Handle modal close
  const handleModalClose = () => {
    setShowIndividualModal(false)
    setSelectedSystemUser('')
    setSelectedRecipient('')
  }

  // Update events when new data comes in
  useEffect(() => {
    if (eventsData) {
      if (currentCursor) {
        // Append to existing events
        setAllEvents((prev) => [...prev, ...(eventsData.data || [])])
      } else {
        // Replace with new events (filter change)
        setAllEvents(eventsData.data || [])
      }
    }
  }, [eventsData, currentCursor])

  // Loading state
  if (isUserLoading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading user details...</span>
          </Spinner>
          <div className="mt-2">Loading user details...</div>
        </div>
      </Container>
    )
  }

  // Error states
  if (userError) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading User</Alert.Heading>
          <p>Failed to load user details. Please try again.</p>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => history.goBack()}
          >
            Go Back
          </button>
        </Alert>
      </Container>
    )
  }

  if (!phoneNumber) {
    return (
      <Container fluid className="p-4">
        <Alert variant="warning">
          <Alert.Heading>No Phone Number Found</Alert.Heading>
          <p>This user doesn't have a phone number assigned yet.</p>
          <button
            type="button"
            className="btn btn-outline-warning"
            onClick={() => history.goBack()}
          >
            Go Back
          </button>
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="mb-4">
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/users">Users</Breadcrumb.Item>
        <Breadcrumb.Item href={`/user/${uuid}`}>
          {userDetail?.user?.name || 'User Detail'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>View Response Limited Events</Breadcrumb.Item>
      </Breadcrumb>

      {/* Filters */}
      <Row className="mb-4">
        <Col>
          <PermissionEventsFilters
            filters={filters}
            onFiltersChange={handleFilterChange}
            senderNumber={phoneNumber}
            loading={isEventsLoading}
          />
        </Col>
      </Row>

      {/* Events Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Permission Events
                {allEvents.length > 0 && (
                  <Badge variant="secondary" className="ms-2">
                    {allEvents.length} loaded
                  </Badge>
                )}
              </h5>
              {eventsData && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => refetchEvents()}
                  disabled={isEventsLoading}
                >
                  {isEventsLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Refreshing...
                    </>
                  ) : (
                    '↻ Refresh'
                  )}
                </button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {eventsError ? (
                <Alert variant="danger" className="m-3">
                  <Alert.Heading>Error Loading Events</Alert.Heading>
                  <p>
                    Failed to load permission events:{' '}
                    {(eventsError as Error)?.message || 'Unknown error'}
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => refetchEvents()}
                  >
                    Try Again
                  </button>
                </Alert>
              ) : (
                <PermissionEventsTable
                  events={allEvents}
                  loading={isEventsLoading}
                  onLoadMore={handleLoadMore}
                  hasMore={eventsData?.pagination.hasMore}
                  onEventClick={handleEventClick}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Individual Behavioral Profile Modal */}
      <IndividualBehavioralProfileModal
        show={showIndividualModal}
        onHide={handleModalClose}
        profile={individualProfileData?.profile || null}
        loading={isIndividualProfileLoading}
        error={individualProfileError as Error | null}
        systemUser={selectedSystemUser}
        recipient={selectedRecipient}
      />
    </Container>
  )
}

export default UserPermissionEventsPage
